import type { Community } from '@/types/v2/community';
import axios from '@/lib/v2/axios';
import type { User } from '@/types/v2/user';

export async function getCommunities({ withRoles = false }: { withRoles?: boolean } = {}): Promise<{
  communities: Community[];
}> {
  const response = await axios.get(`/api/v2/communities?include=unitTypes${withRoles ? '&withRoles=true' : ''}`);
  return response.data;
}

export async function getCommunity(id: string): Promise<{ community: Community }> {
  const response = await axios.get(`/api/v2/communities/${id}?include=unitTypes`);
  return response.data;
}

export interface CreateCommunityData {
  name: string;
}

export async function createCommunity(data: CreateCommunityData): Promise<Community> {
  const response = await axios.post('/api/v2/communities', data);
  return response.data;
}

export interface UpdateCommunityData {
  name: string;
  email: string;
  phone: string;
  address: string;
  address2?: string | null;
  city: string;
  state: string;
  country?: string | null;
  zip_code: string;
  unit_types?: string[] | null;
}

export async function updateCommunity(
  communityId: string,
  data: UpdateCommunityData
): Promise<Community> {
  const response = await axios.put(`/api/v2/communities/${communityId}`, data);
  return response.data;
}

export async function updateCommunityReservationPage(
  communityId: string,
  data: { amenity_reservations_description: string | null }
): Promise<Community> {
  const response = await axios.patch(
    `/api/v2/communities/${communityId}/reservation-page`,
    data
  );
  return response.data;
}

export interface UpdateCommunityPreferencesData {
  amenity_reservations_description?: string | null;
  discussion_page_enabled?: boolean;
  payments_enabled?: boolean;
}

export async function updateCommunityPreferences(
  communityId: string,
  data: UpdateCommunityPreferencesData
): Promise<Community> {
  const response = await axios.patch(
    `/api/v2/communities/${communityId}/preferences`,
    data
  );
  return response.data;
}

export async function getAvailableCommunityResidents(
  communityId: string,
  unitId: string,
): Promise<{ residents: User[] }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/residents?unitId=${unitId}`);
  return response.data;
}

export interface ActivityLog {
  id: string;
  log_name: string | null;
  description: string;
  properties: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  causer?: {
    id: string;
    name: string;
    email: string;
  };
  subject?: {
    id: string | null;
    type: string;
    name: string;
  };
}

export async function getCommunityActivityLogs(
  communityId: string,
  params: {
    per_page?: number;
    page?: number;
    sort?: string;
  } = {}
): Promise<{
  activities: ActivityLog[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_more: boolean;
    next_page: string | null;
    prev_page: string | null;
  };
}> {
  const response = await axios.get(`/api/v2/communities/${communityId}/activity`, { params });
  return response.data;
}

// Custom fields (resident profile fields defined per community)
export interface CommunityCustomField {
  id: string;
  community_id: string;
  name: string;
  key: string;
  visible_to_member: boolean;
  editable_by_member: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export async function getCommunityCustomFields(
  communityId: string
): Promise<{ data: CommunityCustomField[] }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/custom-fields`);
  return response.data;
}

export async function createCommunityCustomField(
  communityId: string,
  data: { name: string; visible_to_member?: boolean; editable_by_member?: boolean }
): Promise<{ data: CommunityCustomField }> {
  const response = await axios.post(`/api/v2/communities/${communityId}/custom-fields`, data);
  return response.data;
}

export async function updateCommunityCustomField(
  communityId: string,
  customFieldId: string,
  data: { name?: string; visible_to_member?: boolean; editable_by_member?: boolean }
): Promise<{ data: CommunityCustomField }> {
  const response = await axios.patch(
    `/api/v2/communities/${communityId}/custom-fields/${customFieldId}`,
    data
  );
  return response.data;
}

export async function deleteCommunityCustomField(
  communityId: string,
  customFieldId: string
): Promise<void> {
  await axios.delete(`/api/v2/communities/${communityId}/custom-fields/${customFieldId}`);
}

export interface StripeConnectStatus {
  payments_enabled: boolean;
  connected: boolean;
  onboarding_complete: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  account_id: string | null;
  business_name: string | null;
  email: string | null;
  bank_name: string | null;
  bank_last4: string | null;
}

export async function getStripeConnectStatus(communityId: string): Promise<StripeConnectStatus> {
  const response = await axios.get(`/api/v2/communities/${communityId}/connect/status`);
  return response.data;
}

export async function startStripeConnectOnboarding(communityId: string): Promise<{ url: string }> {
  const response = await axios.post(`/api/v2/communities/${communityId}/connect/onboard`);
  return response.data;
}

export async function disconnectStripeConnect(communityId: string): Promise<void> {
  await axios.post(`/api/v2/communities/${communityId}/connect/disconnect`);
}

export async function completeStripeConnectCallback(
  communityId: string,
  code: string,
): Promise<StripeConnectStatus & { message: string }> {
  const response = await axios.post(`/api/v2/communities/${communityId}/connect/callback`, { code });
  return response.data;
}

export interface Transaction {
  id: string;
  type: string;
  amount: number;
  fee: number;
  net: number;
  currency: string;
  status: string;
  description: string | null;
  created: number;
  available_on: number;
  source_id: string | null;
  source_type: string | null;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  has_more: boolean;
  first_id: string | null;
  last_id: string | null;
  message?: string;
}

export async function getCommunityTransactions(
  communityId: string,
  params: {
    limit?: number;
    starting_after?: string;
    ending_before?: string;
    created_after?: string;
    created_before?: string;
  } = {}
): Promise<TransactionsResponse> {
  const response = await axios.get(`/api/v2/communities/${communityId}/transactions`, { params });
  return response.data;
}
